/**
 * Lightweight block metadata — no default seeds, migrations, or render imports.
 */
import { BLOCK_PRESENTATION } from '../Blocks/shared/composition/constants'

export const BLOCK_CATEGORIES = [
  { id: 'blocks', label: 'Blocks' },
  { id: 'writing', label: 'Writing' },
  { id: 'organization', label: 'Structure' },
  { id: 'visual', label: 'Visual' },
  { id: 'tracking', label: 'Tracking' },
  { id: 'advanced', label: 'Advanced' },
]

export const BLOCKS_REGISTRY = {
  block_card: {
    type: 'block_card',
    label: 'Core card',
    description: 'Primary idea card with mantra and points',
    icon: '🃏',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'Card',
    implemented: true,
    presentation: BLOCK_PRESENTATION.STANDALONE,
  },
  block_supporting_notes: {
    type: 'block_supporting_notes',
    label: 'Supporting notes',
    description: 'Secondary card for extra points and detail',
    icon: '📎',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'SupportingNotesCard',
    implemented: true,
    presentation: BLOCK_PRESENTATION.STANDALONE,
  },
  block_badge: {
    type: 'block_badge',
    label: 'Badge',
    description: 'Legacy chip block — use right-click on any block title instead',
    icon: '🏷',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'Badge',
    implemented: true,
    launcherHidden: true,
  },
  block_callout: {
    type: 'block_callout',
    label: 'Callout',
    description: 'Highlighted tip or warning',
    icon: '💬',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'Callout',
    implemented: true,
    presentation: BLOCK_PRESENTATION.EMBEDDED,
    embeddable: true,
  },
  block_quote: {
    type: 'block_quote',
    label: 'Quote',
    description: 'Editorial quote emphasis',
    icon: '❝',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'Quote',
    implemented: true,
    presentation: BLOCK_PRESENTATION.EMBEDDED,
    embeddable: true,
  },
  block_code: {
    type: 'block_code',
    label: 'Code',
    description: 'Syntax-highlighted code snippet',
    icon: '{ }',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'CodeBlock',
    implemented: true,
    presentation: BLOCK_PRESENTATION.STANDALONE,
    embeddable: true,
  },
  block_media: {
    type: 'block_media',
    label: 'Media',
    description: 'Visual composition grid with insights',
    icon: '🖼',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'MediaBlock',
    implemented: true,
    presentation: BLOCK_PRESENTATION.STANDALONE,
    embeddable: true,
  },
  block_rule: {
    type: 'block_rule',
    label: 'Checklist',
    description: 'Rule lines and checklist points',
    icon: '▸',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'Rule',
    implemented: true,
    presentation: BLOCK_PRESENTATION.STANDALONE,
    embeddable: true,
  },
  block_zone_grid: {
    type: 'block_zone_grid',
    label: 'Zone Grid',
    description: 'Three-column code zones',
    icon: '▦',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'ZoneGrid',
    implemented: true,
  },
  block_warning_box: {
    type: 'block_warning_box',
    label: 'Warning Box',
    description: 'Amber alert panel',
    icon: '⚠',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'WarningBox',
    implemented: true,
    presentation: BLOCK_PRESENTATION.EMBEDDED,
    embeddable: true,
  },
  block_daily_row: {
    type: 'block_daily_row',
    label: 'Daily Schedule',
    description: 'Three time blocks in a row',
    icon: '⏱',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'DailyRow',
    implemented: true,
  },
  block_step_row: {
    type: 'block_step_row',
    label: 'Step Row',
    description: 'Numbered step with detail',
    icon: '①',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'StepRow',
    implemented: true,
  },
  block_steps: {
    type: 'block_steps',
    label: 'Workflow',
    description: 'Interactive workflow steps',
    icon: '→',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'Steps',
    implemented: true,
    presentation: BLOCK_PRESENTATION.STANDALONE,
    embeddable: true,
  },
  block_hero: {
    type: 'block_hero',
    label: 'Hero',
    description: 'Notebook hero banner',
    icon: '★',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'Hero',
    implemented: true,
  },
  block_tabs: {
    type: 'block_tabs',
    label: 'Tabs',
    description: 'Tab strip preview',
    icon: '⊟',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'Tabs',
    implemented: true,
  },
  block_richtext: {
    type: 'block_richtext',
    label: 'Rich Text',
    description: 'Freeform document canvas with inline blocks',
    icon: 'T',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'RichText',
    implemented: true,
    embeddable: false,
  },
  block_insight: {
    type: 'block_insight',
    label: 'Insight',
    description: 'Editable data charts with conclusions',
    icon: '💡',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'InsightBlock',
    implemented: true,
    presentation: BLOCK_PRESENTATION.STANDALONE,
    embeddable: true,
  },
  block_summary: {
    type: 'block_summary',
    label: 'Summary',
    description: 'Summary block',
    icon: '📝',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'SummaryBlock',
    implemented: true,
    embeddable: true,
    launcherHidden: true,
  },
  block_question: {
    type: 'block_question',
    label: 'Question',
    description: 'Interview-style question',
    icon: '?',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'QuestionBlock',
    implemented: true,
    embeddable: true,
    launcherHidden: true,
  },
  block_reference: {
    type: 'block_reference',
    label: 'Reference',
    description: 'Dynamic link references with metadata and notes',
    icon: '🔗',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'ReferenceBlock',
    implemented: true,
    presentation: BLOCK_PRESENTATION.STANDALONE,
    embeddable: true,
  },
  block_timeline: {
    type: 'block_timeline',
    label: 'Timeline',
    description: 'Ordered timeline events',
    icon: '📅',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'TimelineBlock',
    implemented: true,
    embeddable: true,
  },
  block_interviewer_test: {
    type: 'block_interviewer_test',
    label: 'Interviewer Test',
    description: 'Interview prep checklist',
    icon: '🎯',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'InterviewerTest',
    implemented: true,
  },
  block_lesson_tracker: {
    type: 'block_lesson_tracker',
    label: 'Lesson Tracker',
    description: '30-lesson progress grid',
    icon: '📚',
    category: 'blocks',
    launcherCategory: 'blocks',
    component: 'LessonTracker',
    implemented: true,
  },
}
